import { sendAFEvent, setAFCustomerId } from './appsflyer';
import { sendAmplitudeData, setAmplitudeUserId, setAmplitudeUserProperties } from './amplitude';
import { sendBingFunnelEvent, sendBingPurchaseEvent } from './bing';
import { sendLockerStudioData } from './lockerStudio';
import { logPurchaseEvent } from './eventLogger';
import {
  IS_DEV,
  IS_PROD,
  GOOGLE_MEASUREMENT_ID,
  GOOGLE_ADS_ID,
  GOOGLE_ADS_CONVERSION_ID,
  GOOGLE_ADS_LTV_CONVERSION_LABEL,
  GOOGLE_ADS_LTV_ID,
  GOOGLE_ADS_LTV_ID_1,
  GOOGLE_ADS_LTV_ID_2,
  GOOGLE_ADS_LTV_ID_3,
  GOOGLE_ADS_LTV_ID_4,
  GOOGLE_ADS_LTV_ID_5,
  GOOGLE_ADS_LTV_CONVERSION_LABEL_1,
  GOOGLE_ADS_LTV_CONVERSION_LABEL_2,
  GOOGLE_ADS_LTV_CONVERSION_LABEL_3,
  GOOGLE_ADS_LTV_CONVERSION_LABEL_4,
  GOOGLE_ADS_LTV_CONVERSION_LABEL_5,
} from '../core/constants';
import { sendGoogleTagEvent } from './googleTag';
import {
  sendReviewPurchaseConversion,
  normalizePurchaseAmountForMeta,
  formatPurchaseAmountStringForMeta,
  normalizeCurrencyCodeForMeta,
  type GoogleAdsUserData,
  withGoogleAdsUserData,
  isPLValueLanding,
  resolvePLTVCountry,
} from '../core/utils';
import { getSearchLanguage, parseOnboardingPath } from '../locales/languageConfig';

export const sendAnalyticsEvents = (eventName: string, eventParams: any) => {
  sendAFEvent(eventName, eventParams);
  sendAmplitudeData(eventName, eventParams);
  sendLockerStudioData(eventName, eventParams);
  pushGoogleTag(eventName, eventParams);
  sendBingFunnelEvent(eventName);
};

const googleAdsLTVConversions = [
  { id: GOOGLE_ADS_LTV_ID, label: GOOGLE_ADS_LTV_CONVERSION_LABEL },
  { id: GOOGLE_ADS_LTV_ID_1, label: GOOGLE_ADS_LTV_CONVERSION_LABEL_1 },
  { id: GOOGLE_ADS_LTV_ID_2, label: GOOGLE_ADS_LTV_CONVERSION_LABEL_2 },
  { id: GOOGLE_ADS_LTV_ID_3, label: GOOGLE_ADS_LTV_CONVERSION_LABEL_3 },
  { id: GOOGLE_ADS_LTV_ID_4, label: GOOGLE_ADS_LTV_CONVERSION_LABEL_4 },
  { id: GOOGLE_ADS_LTV_ID_5, label: GOOGLE_ADS_LTV_CONVERSION_LABEL_5 },
];

interface PurchaseEventProps {
  price: number;
  currency?: string;
  plan: string;
  sub_id: string;
  event_id: string;
  gclid?: string;
  pixelName: string;
  flowType?: string | null;
  landing?: string | null;
  pLTVPrice?: number;
  userLocation?: string | null;
  userData?: GoogleAdsUserData;
}

export const sendPurchaseEvent = ({
  price,
  currency,
  plan,
  sub_id,
  event_id,
  gclid,
  pixelName,
  landing,
  pLTVPrice,
  userLocation,
  userData,
}: PurchaseEventProps) => {
  if (pLTVPrice !== undefined) {
    const { language, linkCountry } = parseOnboardingPath(window.location.pathname);
    const linkLanguage = language ?? getSearchLanguage(window.location.search);
    const { country, source } = resolvePLTVCountry(userLocation, linkCountry);
    setAmplitudeUserProperties({
      link_language: linkLanguage ?? 'none',
      link_country: linkCountry ?? 'none',
      browser_language: navigator.language,
      detected_geo: userLocation || 'none',
      pltv_country: country ?? 'none',
      pltv_country_source: source,
    });
  }

  const priceForMetaServer = formatPurchaseAmountStringForMeta(price);
  const normalizedPrice = normalizePurchaseAmountForMeta(price);
  const normalizedCurrency = normalizeCurrencyCodeForMeta(currency);
  const shouldSuppressMainConversion = isPLValueLanding(landing);
  const pLTVValue = pLTVPrice != null && Number.isFinite(pLTVPrice) ? pLTVPrice : undefined;

  sendBingPurchaseEvent({ price, currency, eventId: event_id });

  if (pixelName === 'fb') {
    logPurchaseEvent({
      plan,
      price: normalizedPrice,
      currency: normalizedCurrency,
      transactionId: sub_id,
      eventId: event_id,
    });
    pushFBEvent(
      {
        currency: normalizedCurrency,
        itemCount: 1,
        transactionId: sub_id,
        price: priceForMetaServer,
        value: priceForMetaServer,
      },
      event_id,
    );
  } else if (pixelName === 'ttq') {
    window?.ttq?.track('CompletePayment', {
      value: normalizedPrice,
      currency: normalizedCurrency,
      contents: [
        {
          content_type: 'product',
          content_name: plan,
        },
      ],
      event_id,
    });
  }

  if (!IS_DEV) {
    sendGoogleTagEvent('purchase', {
      currency: normalizedCurrency,
      value: normalizedPrice,
      transaction_id: sub_id,
      gclid,
      items: {
        item_id: plan,
        item_name: plan,
      },

      send_to: GOOGLE_MEASUREMENT_ID,
    });
    if (GOOGLE_ADS_ID && !shouldSuppressMainConversion) {
      sendGoogleTagEvent(
        'conversion',
        withGoogleAdsUserData(
          {
            send_to: `${GOOGLE_ADS_ID}/${GOOGLE_ADS_CONVERSION_ID}`,
            value: normalizedPrice,
            currency: normalizedCurrency,
            transaction_id: sub_id,
          },
          userData,
        ),
      );
    }
    sendReviewPurchaseConversion({
      transactionId: sub_id,
      value: normalizedPrice,
      currency: normalizedCurrency,
      userData,
    });
    if (pLTVValue !== undefined) {
      const ltvValue = normalizePurchaseAmountForMeta(pLTVValue);
      googleAdsLTVConversions.forEach(({ id, label }) => {
        if (!id || !label) return;
        sendGoogleTagEvent(
          'conversion',
          withGoogleAdsUserData(
            {
              send_to: `${id}/${label}`,
              value: ltvValue,
              currency: 'USD',
              transaction_id: sub_id,
            },
            userData,
          ),
        );
      });
    }
  }
};

export const setAnalyticsUserId = (userId: string) => {
  setAmplitudeUserId(userId);
  setAFCustomerId(userId);
};

export const pushGoogleTag = (
  eventName: string,
  eventParams: any,
  tagId = GOOGLE_MEASUREMENT_ID,
) => {
  if (IS_DEV) return;
  sendGoogleTagEvent(eventName, { ...eventParams, send_to: tagId });
};

export const pushFBEvent = (data: any, eventId: string) => {
  window.fbq?.('track', 'Purchase' + (!IS_PROD ? '_test' : ''), data, { eventID: eventId });
};
